import React, { useEffect, useState } from 'react';
import {
  useGetUserQuery,
  useGoNextMutation,
  useInvitationsMutation,
  usePostOrganizationsMutation,
  usePutOrganizationsMutation,
  useGetOrganizationByIdQuery,
} from 'redux/apiSlices';
import useForm from 'hooks/useForm';
import { useNavigate } from 'react-router-dom';
import { RoutesPaths } from 'routes/Routes.types';
import { sendGTMDataLayer } from 'helpers/utils';
import { setUser } from 'redux/slices';
import { useAppDispatch } from 'redux/store';
import AuthFlowLayout from '../public/authFlow/AuthFlowLayout/AuthFlowLayout';
import StepRail from '../public/authFlow/StepRail/StepRail';
import InviteStep from './InviteStep/InviteStep';
import OrganizationStep from './OrganizationStep/OrganizationStep';
import { OnboardState } from './Onboarding.type';
import { SOnboardingCard } from './onboarding.styles';

const stepMapping: Record<string, number> = {
  [OnboardState.USER_INVITES]: 1,
};

const errMessage = (e: unknown): string | undefined =>
  (e as { data?: { message?: string } })?.data?.message;

const Onboarding = () => {
  const { data, isLoading, refetch } = useGetUserQuery(true);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const step = data?.organization?.onboardState;
  const id = data?.organization?.id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [
    postOrganizations,
    {
      isSuccess: isSuccessOrganization,
      error: postError,
      isLoading: postLoading,
    },
  ] = usePostOrganizationsMutation();
  const [putOrganizations, { error: putError, isLoading: putLoading }] =
    usePutOrganizationsMutation();
  const { data: getOrgData, isLoading: orgLoading } =
    useGetOrganizationByIdQuery(id, { skip: !id });
  const [invitations, { error: inviteError, isLoading: inviteLoading }] =
    useInvitationsMutation();
  const [goNext] = useGoNextMutation();

  const { form: organizationForm } = useForm();
  const { form: invitationForm } = useForm();

  const handleFinishOrgStep = async () => {
    try {
      const orgData = await organizationForm.validateFields();
      // Design shows two address lines; the backend expects a single `address`.
      // `size` comes from the required Company Size field (in ...orgData).
      const address = [orgData.address, orgData.address2]
        .filter(Boolean)
        .join(', ');
      const payload = { ...orgData, address };
      delete payload.address2;

      if (step === OnboardState.USER_INVITES) {
        await putOrganizations({ ...payload, id }).unwrap();
        setCurrentStep(1);
      } else {
        await postOrganizations(payload).unwrap();
        sendGTMDataLayer({
          event: 'org_created',
          emailAddress: data?.email,
          fullName: data?.fullName,
        });
      }
    } catch (err) {
      // validation errors render inline per field; request errors (post/put)
      // surface via the OrganizationStep Alert.
    }
  };

  // goNext flips onboardState to ONBOARDED server-side. Refetch the user and
  // push the fresh state into redux BEFORE navigating — otherwise the route
  // guard still reads USER_INVITES and bounces straight back to /onboarding.
  const finishOnboarding = async () => {
    const fresh = await refetch().unwrap();
    dispatch(setUser(fresh));
    navigate(`/${RoutesPaths.Workfeed}`);
  };

  const handleFinishInviteStep = async () => {
    try {
      const inviteData = await invitationForm.validateFields();
      // .unwrap() so a rejected invite/goNext throws here (surfaced via the
      // Alert) instead of resolving with { error } and advancing on failure.
      await invitations(inviteData.invitation).unwrap();
      await goNext(id).unwrap();
      sendGTMDataLayer({
        event: 'invite_users_done',
        emailAddress: data?.email,
        fullName: data?.fullName,
      });
      await finishOnboarding();
    } catch (err) {
      // request error — surfaced via the InviteStep Alert
    }
  };

  const handleSkip = async () => {
    try {
      await goNext(id).unwrap();
      sendGTMDataLayer({
        event: 'skip_invite_users',
        emailAddress: data?.email,
        fullName: data?.fullName,
      });
      await finishOnboarding();
    } catch (err) {
      // request error — surfaced via the InviteStep Alert
    }
  };

  useEffect(() => {
    refetch();
  }, [isSuccessOrganization]);

  useEffect(() => {
    if (step) setCurrentStep(stepMapping[step] || 0);
  }, [step]);

  if (isLoading) return null;

  // AntD captures <Form initialValues> once on mount and never re-applies, and
  // the reused ImageUpload/WeekDays self-seed from their value on mount too. So
  // for an existing org (e.g. Back from the invite step) wait for the org query
  // before mounting the org form — otherwise it renders blank despite saved data.
  const orgReady = !id || !orgLoading;

  return (
    <AuthFlowLayout title="Configure your workspace">
      <SOnboardingCard>
        <StepRail count={2} current={currentStep} />
        {currentStep === 0 && orgReady && (
          <OrganizationStep
            form={organizationForm}
            initialValues={getOrgData}
            error={errMessage(postError || putError)}
            onNext={handleFinishOrgStep}
            nextLoading={postLoading || putLoading}
          />
        )}
        {currentStep === 1 && (
          <InviteStep
            form={invitationForm}
            error={errMessage(inviteError)}
            onBack={() => setCurrentStep(0)}
            onSkip={handleSkip}
            onSend={handleFinishInviteStep}
            sendLoading={inviteLoading}
          />
        )}
      </SOnboardingCard>
    </AuthFlowLayout>
  );
};

export default Onboarding;
